// =====================================================
// AngebotKonfigurator.jsx — TEF Angebots-Konfigurator v2
// Tarif-Suche · Geräte-DB · Zusatzoptionen · Coupon · Berechnung
// Live-Ansicht & Druckansicht · o2 Modern Design
// =====================================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import html2pdf from 'html2pdf.js';

const STORAGE_KEY = 'tef-konfigurator-v2';
const LAUFZEIT_MONATE = 24;

function fmtEuro(v) { return (v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }
function generateId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

// ========== GLASS CARD ==========
function GlassCard({ children, glow = 'cyan', style = {}, onClick }) {
  const glowMap = {
    cyan:   { border: 'rgba(56,189,248,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(56,189,248,0.08)' },
    green:  { border: 'rgba(0,245,212,0.18)',   bg: 'rgba(10,25,50,0.55)',  shadow: 'rgba(0,245,212,0.08)' },
    pink:   { border: 'rgba(255,102,162,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(255,102,162,0.08)' },
    purple: { border: 'rgba(167,139,250,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(167,139,250,0.08)' },
    orange: { border: 'rgba(251,146,60,0.18)',  bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(251,146,60,0.08)' },
  };
  const c = glowMap[glow] || glowMap.cyan;
  return (
    <div onClick={onClick} style={{ borderRadius: 20, border: `1px solid ${c.border}`, background: c.bg, backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', boxShadow: `0 8px 32px ${c.shadow}, inset 0 1px 0 rgba(255,255,255,0.06)`, padding: '18px 16px', ...style }}>
      {children}
    </div>
  );
}

function SectionLabel({ children, icon }) {
  return (
    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'rgba(200,220,255,0.82)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon && <span>{icon}</span>}{children}
    </div>
  );
}

function InputField({ label, value, onChange, type = 'text', placeholder = '', style = {} }) {
  return (
    <div style={{ marginBottom: 8 }}>
      {label && <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'rgba(200,220,255,0.72)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>}
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '0.8rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', ...style }} />
    </div>
  );
}

// ========== MAIN COMPONENT ==========
export function AngebotKonfigurator() {
  // Data
  const [tariffs, setTariffs] = useState([]);
  const [smartphones, setSmartphones] = useState([]);
  const [routers, setRouters] = useState([]);
  const [zusatzoptionen, setZusatzoptionen] = useState([]);

  // Selections
  const [selectedTariffs, setSelectedTariffs] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceStorage, setDeviceStorage] = useState('');
  const [deviceColor, setDeviceColor] = useState('');
  const [devicePayment, setDevicePayment] = useState('vertrag');
  const [couponValue, setCouponValue] = useState('');
  const [couponEnabled, setCouponEnabled] = useState(true);
  const [barPrice, setBarPrice] = useState('');

  // Customer
  const [kundenName, setKundenName] = useState('');
  const [notiz, setNotiz] = useState('');

  // UI
  const [tarifSearch, setTarifSearch] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [showDevicePicker, setShowDevicePicker] = useState(false);
  const [savedOffers, setSavedOffers] = useState([]);
  const [activeTab, setActiveTab] = useState('config');
  const printRef = useRef(null);

  // Load data
  useEffect(() => {
    Promise.all([
      fetch('/api/tariffs').then(r => r.json()).catch(() => []),
      fetch('/api/smartphones').then(r => r.json()).catch(() => []),
      fetch('/api/routers').then(r => r.json()).catch(() => []),
      fetch('/api/options').then(r => r.json()).catch(() => []),
    ]).then(([t, s, r, z]) => {
      setTariffs(t);
      setSmartphones(s.filter(d => d.active !== false));
      setRouters(r.filter(d => d.active !== false));
      setZusatzoptionen(z);
    });
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      setSavedOffers(saved);
    } catch {}
  }, []);

  // Tariff filtering
  const filteredTariffs = useMemo(() => {
    if (!tarifSearch.trim()) return [];
    const q = tarifSearch.toLowerCase();
    return tariffs.filter(t =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q) ||
      (t.segment || '').toLowerCase().includes(q)
    ).slice(0, 15);
  }, [tariffs, tarifSearch]);

  // Device filtering
  const filteredDevices = useMemo(() => {
    const q = deviceSearch.toLowerCase();
    const allDevices = [...smartphones, ...routers.filter(r => r.type !== 'repeater')];
    if (!q) return allDevices.slice(0, 20);
    return allDevices.filter(d =>
      (d.name || '').toLowerCase().includes(q) ||
      (d.brand || '').toLowerCase().includes(q)
    ).slice(0, 20);
  }, [smartphones, routers, deviceSearch]);

  const addTariff = useCallback((tariff) => {
    if (selectedTariffs.length >= 5) return;
    if (selectedTariffs.find(t => t.id === tariff.id)) return;
    setSelectedTariffs(prev => [...prev, { ...tariff, _konfId: generateId() }]);
    setTarifSearch('');
  }, [selectedTariffs]);

  const removeTariff = useCallback((konfId) => {
    setSelectedTariffs(prev => prev.filter(t => t._konfId !== konfId));
  }, []);

  const toggleOption = useCallback((opt) => {
    setSelectedOptions(prev => {
      const exists = prev.find(o => o.id === opt.id);
      if (exists) return prev.filter(o => o.id !== opt.id);
      if (prev.length >= 10) return prev;
      return [...prev, opt];
    });
  }, []);

  const selectDevice = useCallback((device) => {
    setSelectedDevice(device);
    if (device.storage && device.storage.length > 0) setDeviceStorage(String(device.storage[0]));
    if (device.colors && device.colors.length > 0) setDeviceColor(device.colors[0]);
    setShowDevicePicker(false);
    setDeviceSearch('');
  }, []);

  // ========== CALCULATIONS ==========
  const calc = useMemo(() => {
    const monthlyTariffTotal = selectedTariffs.reduce((sum, t) => sum + (t.promoPrice || t.price || 0), 0);
    const connectionFeeTotal = selectedTariffs.reduce((sum, t) => sum + (t.connectionFee || 0), 0);
    const monthlyOptionsTotal = selectedOptions.reduce((sum, o) => sum + (o.price || 0), 0);
    const monthlyTotal = monthlyTariffTotal + monthlyOptionsTotal;

    let devicePrice = 0;
    if (selectedDevice) {
      if (selectedDevice.prices && deviceStorage) devicePrice = selectedDevice.prices[deviceStorage] || 0;
      else devicePrice = selectedDevice.price || selectedDevice.purchasePrice || 0;
    }
    const deviceMonthly = selectedDevice?.monthlyPrices?.[deviceStorage] || 0;
    const barPriceNum = parseFloat(barPrice) || devicePrice;
    const couponVal = couponEnabled ? (parseFloat(couponValue) || 0) : 0;

    const totalTariff24 = monthlyTotal * LAUFZEIT_MONATE;
    const totalWithConnection = totalTariff24 + connectionFeeTotal;
    const totalWithDevice = totalWithConnection + (deviceMonthly * LAUFZEIT_MONATE);
    const totalMinusCoupon = totalWithDevice - couponVal;

    const costPerDay = monthlyTotal / 30;
    const costPerWeek = monthlyTotal / 4.33;
    const costPerMonth = monthlyTotal;
    const costPerYear = monthlyTotal * 12;
    const cost2Years = totalWithConnection;

    const vertragDeviceCost = deviceMonthly * LAUFZEIT_MONATE;
    const barDifference = barPriceNum - vertragDeviceCost;
    const isBarcheaper = barDifference < 0;

    return { monthlyTariffTotal, connectionFeeTotal, monthlyOptionsTotal, monthlyTotal, devicePrice, deviceMonthly, barPriceNum, couponVal, totalTariff24, totalWithConnection, totalWithDevice, totalMinusCoupon, costPerDay, costPerWeek, costPerMonth, costPerYear, cost2Years, vertragDeviceCost, barDifference, isBarcheaper };
  }, [selectedTariffs, selectedOptions, selectedDevice, deviceStorage, barPrice, couponValue, couponEnabled]);

  const handleSave = useCallback(() => {
    const offer = {
      id: generateId(), timestamp: new Date().toISOString(), kundenName: kundenName || 'Unbenannt', notiz,
      selectedTariffs: selectedTariffs.map(t => ({ id: t.id, title: t.title, price: t.promoPrice || t.price, connectionFee: t.connectionFee })),
      selectedOptions: selectedOptions.map(o => ({ id: o.id, name: o.name, price: o.price })),
      device: selectedDevice ? { id: selectedDevice.id, name: selectedDevice.name, storage: deviceStorage, color: deviceColor, price: calc.devicePrice } : null,
      devicePayment, couponValue: calc.couponVal, calc: { ...calc },
    };
    const updated = [offer, ...savedOffers].slice(0, 100);
    setSavedOffers(updated);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch {}
  }, [kundenName, notiz, selectedTariffs, selectedOptions, selectedDevice, deviceStorage, deviceColor, devicePayment, calc, savedOffers]);

  const handleDelete = useCallback((id) => {
    const updated = savedOffers.filter(o => o.id !== id);
    setSavedOffers(updated);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch {}
  }, [savedOffers]);

  const handleClear = useCallback(() => {
    setSelectedTariffs([]); setSelectedOptions([]); setSelectedDevice(null);
    setDeviceStorage(''); setDeviceColor(''); setBarPrice(''); setCouponValue('');
    setKundenName(''); setNotiz('');
  }, []);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownloadPDF = async () => {
    setActiveTab('preview');
    setIsGeneratingPdf(true);
    // Kurz warten, bis Tab gewechselt hat und State angewendet wurde
    await new Promise(r => setTimeout(r, 100));
    
    const element = printRef.current;
    if (!element) {
      setIsGeneratingPdf(false);
      return;
    }

    const opt = {
      margin:       10, // mm
      filename:     `TEF-Angebot-${(kundenName || 'Kunde').replace(/\s+/g,'-')}-${new Date().toISOString().slice(0,10)}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error('[PDF Export] Error:', err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const tabs = [
    { id: 'config', label: '⚙️ Konfiguration' },
    { id: 'preview', label: '👁️ Liveansicht' },
    { id: 'saved', label: `📂 Gespeichert (${savedOffers.length})` },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', color: '#fff', fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '12px 16px 100px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, background: 'linear-gradient(135deg, #38bdf8, #00f5d4, #ff66a2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>📋 Angebot Konfigurator</div>
          <div style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.72)', marginTop: 4 }}>Erstelle individuelle Angebote mit Tarifberechnung</div>
        </div>

        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 4, border: '1px solid rgba(255,255,255,0.06)' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ flex: 1, padding: '10px 8px', borderRadius: 10, border: 'none', background: activeTab === tab.id ? 'rgba(56,189,248,0.15)' : 'transparent', color: activeTab === tab.id ? '#38bdf8' : 'rgba(200,220,255,0.65)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ========== KONFIGURATION TAB ========== */}
        {activeTab === 'config' && (<>
          {/* Kundendaten */}
          <GlassCard glow="cyan" style={{ marginBottom: 14 }}>
            <SectionLabel icon="👤">Kundendaten</SectionLabel>
            <InputField label="Name" value={kundenName} onChange={setKundenName} placeholder="Kundenname eingeben" />
            <InputField label="Notiz" value={notiz} onChange={setNotiz} placeholder="Optionale Notiz..." />
          </GlassCard>

          {/* Tarifauswahl */}
          <GlassCard glow="cyan" style={{ marginBottom: 14 }}>
            <SectionLabel icon="📶">Tarife ({selectedTariffs.length}/5)</SectionLabel>
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <input type="text" value={tarifSearch} onChange={e => setTarifSearch(e.target.value)} placeholder="Tarif suchen..."
                style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(56,189,248,0.2)', background: 'rgba(56,189,248,0.04)', color: '#fff', fontSize: '0.78rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
              {filteredTariffs.length > 0 && tarifSearch && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, maxHeight: 240, overflowY: 'auto', background: 'rgba(10,20,40,0.96)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 12, marginTop: 4, boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
                  {filteredTariffs.map(t => (
                    <div key={t.id} onClick={() => addTariff(t)}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(56,189,248,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fff' }}>{t.title}</div>
                        <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.65)' }}>{t.category} · {t.segment} · {typeof t.dataLabel === 'number' ? t.dataLabel + ' GB' : t.dataLabel}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {t.promoPrice && <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(t.promoPrice)}</div>}
                        {t.price && t.promoPrice && <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through' }}>{fmtEuro(t.price)}</div>}
                        {!t.promoPrice && t.price && <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff' }}>{fmtEuro(t.price)}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedTariffs.map(t => (
              <div key={t._konfId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fff' }}>{t.title}</div>
                  <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.65)' }}>{typeof t.dataLabel === 'number' ? t.dataLabel + ' GB' : t.dataLabel} · AG {fmtEuro(t.connectionFee)}</div>
                </div>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(t.promoPrice || t.price)}/mtl.</div>
                <button onClick={() => removeTariff(t._konfId)} style={{ background: 'none', border: 'none', color: '#ff006e', cursor: 'pointer', fontSize: '0.9rem', padding: '2px 6px' }}>✕</button>
              </div>
            ))}
            {selectedTariffs.length === 0 && (
              <div style={{ textAlign: 'center', padding: '16px 0', color: 'rgba(200,220,255,0.5)', fontSize: '0.7rem' }}>Noch keine Tarife ausgewählt</div>
            )}
          </GlassCard>

          {/* Gerätauswahl */}
          <GlassCard glow="green" style={{ marginBottom: 14 }}>
            <SectionLabel icon="📱">Gerät</SectionLabel>
            {selectedDevice ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 50, height: 50, borderRadius: 12, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>📱</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#fff' }}>{selectedDevice.name}</div>
                    <div style={{ fontSize: '0.58rem', color: 'rgba(200,220,255,0.65)' }}>{selectedDevice.brand} · {selectedDevice.display || selectedDevice.type}</div>
                  </div>
                  <button onClick={() => setSelectedDevice(null)} style={{ background: 'none', border: 'none', color: '#ff006e', cursor: 'pointer', fontSize: '0.85rem' }}>✕</button>
                </div>
                {selectedDevice.storage && selectedDevice.storage.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(200,220,255,0.72)', marginBottom: 4 }}>SPEICHER</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {selectedDevice.storage.map(s => (
                        <button key={s} onClick={() => setDeviceStorage(String(s))}
                          style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${deviceStorage === String(s) ? 'rgba(0,245,212,0.4)' : 'rgba(255,255,255,0.08)'}`, background: deviceStorage === String(s) ? 'rgba(0,245,212,0.1)' : 'rgba(255,255,255,0.03)', color: deviceStorage === String(s) ? '#00f5d4' : 'rgba(200,220,255,0.72)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {s >= 1024 ? `${s/1024} TB` : `${s} GB`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {selectedDevice.colors && selectedDevice.colors.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(200,220,255,0.72)', marginBottom: 4 }}>FARBE</div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {selectedDevice.colors.map(c => (
                        <button key={c} onClick={() => setDeviceColor(c)}
                          style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${deviceColor === c ? 'rgba(0,245,212,0.4)' : 'rgba(255,255,255,0.08)'}`, background: deviceColor === c ? 'rgba(0,245,212,0.1)' : 'rgba(255,255,255,0.03)', color: deviceColor === c ? '#00f5d4' : 'rgba(200,220,255,0.72)', fontSize: '0.6rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: '0.55rem', fontWeight: 700, color: 'rgba(200,220,255,0.72)', marginBottom: 4 }}>BEZAHLUNG</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[{id:'vertrag',label:'📄 Vertrag'},{id:'bar',label:'💰 Barkauf'},{id:'coupon',label:'🎟️ Coupon'}].map(p => (
                      <button key={p.id} onClick={() => setDevicePayment(p.id)}
                        style={{ flex: 1, padding: '8px 6px', borderRadius: 10, border: `1px solid ${devicePayment === p.id ? 'rgba(0,245,212,0.4)' : 'rgba(255,255,255,0.08)'}`, background: devicePayment === p.id ? 'rgba(0,245,212,0.1)' : 'rgba(255,255,255,0.03)', color: devicePayment === p.id ? '#00f5d4' : 'rgba(200,220,255,0.72)', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                {devicePayment === 'bar' && <InputField label="Barkaufpreis (€)" value={barPrice} onChange={setBarPrice} type="number" placeholder={String(calc.devicePrice)} />}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 6 }}>
                  <span style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.72)' }}>Gerätepreis (UVP)</span>
                  <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#fff' }}>{fmtEuro(calc.devicePrice)}</span>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowDevicePicker(!showDevicePicker)}
                style={{ width: '100%', padding: '16px', borderRadius: 14, border: '1px dashed rgba(0,245,212,0.3)', background: 'rgba(0,245,212,0.04)', color: '#00f5d4', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                + Gerät aus Datenbank wählen
              </button>
            )}
            {showDevicePicker && (
              <div style={{ marginTop: 10 }}>
                <input type="text" value={deviceSearch} onChange={e => setDeviceSearch(e.target.value)} placeholder="Gerät suchen..."
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(0,245,212,0.2)', background: 'rgba(0,245,212,0.04)', color: '#fff', fontSize: '0.78rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {filteredDevices.map(d => (
                    <div key={d.id} onClick={() => selectDevice(d)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', marginBottom: 4, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,245,212,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}>
                      <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,245,212,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>📱</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>{d.name}</div>
                        <div style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.6)' }}>{d.brand} · {d.display || d.type}</div>
                      </div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#00f5d4' }}>ab {fmtEuro(d.prices ? Math.min(...Object.values(d.prices)) : (d.price || 0))}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </GlassCard>

          {/* Coupon */}
          <GlassCard glow="orange" style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <SectionLabel icon="🎟️">Coupon / Gutschein</SectionLabel>
              <button onClick={() => setCouponEnabled(!couponEnabled)}
                style={{ padding: '4px 12px', borderRadius: 8, border: `1px solid ${couponEnabled ? 'rgba(0,245,212,0.4)' : 'rgba(255,255,255,0.1)'}`, background: couponEnabled ? 'rgba(0,245,212,0.1)' : 'rgba(255,255,255,0.03)', color: couponEnabled ? '#00f5d4' : 'rgba(200,220,255,0.5)', fontSize: '0.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                {couponEnabled ? '✓ Aktiv' : '○ Inaktiv'}
              </button>
            </div>
            {couponEnabled && <InputField label="Coupon-Wert (€)" value={couponValue} onChange={setCouponValue} type="number" placeholder="z.B. 100" />}
          </GlassCard>

          {/* Zusatzoptionen */}
          <GlassCard glow="purple" style={{ marginBottom: 14 }}>
            <SectionLabel icon="⚡">Zusatzoptionen ({selectedOptions.length}/10)</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
              {zusatzoptionen.map(opt => {
                const sel = !!selectedOptions.find(o => o.id === opt.id);
                return (
                  <button key={opt.id} onClick={() => toggleOption(opt)}
                    style={{ padding: '10px 10px', borderRadius: 12, border: `1px solid ${sel ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.06)'}`, background: sel ? 'rgba(167,139,250,0.1)' : 'rgba(255,255,255,0.02)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 700, color: sel ? '#a78bfa' : 'rgba(200,220,255,0.82)' }}>{opt.name}</div>
                    <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.55)', marginTop: 2 }}>{opt.description}</div>
                    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: opt.price < 0 ? '#00f5d4' : (sel ? '#a78bfa' : 'rgba(200,220,255,0.72)'), marginTop: 4 }}>{fmtEuro(opt.price)}/mtl.</div>
                  </button>
                );
              })}
            </div>
          </GlassCard>

          {/* LIVE CALCULATION */}
          <GlassCard glow="green" style={{ marginBottom: 14, background: 'linear-gradient(135deg, rgba(0,245,212,0.08), rgba(56,189,248,0.06))' }}>
            <SectionLabel icon="📊">Kostenübersicht</SectionLabel>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.75)' }}>Tarif(e) monatlich</span>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>{fmtEuro(calc.monthlyTariffTotal)}</span>
            </div>
            {calc.monthlyOptionsTotal !== 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.75)' }}>Optionen monatlich</span>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: calc.monthlyOptionsTotal < 0 ? '#00f5d4' : '#fff' }}>{fmtEuro(calc.monthlyOptionsTotal)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff' }}>Gesamt monatlich</span>
              <span style={{ fontSize: '1rem', fontWeight: 900, color: '#00f5d4', textShadow: '0 0 12px rgba(0,245,212,0.3)' }}>{fmtEuro(calc.monthlyTotal)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginTop: 12, marginBottom: 12 }}>
              {[{label:'Pro Tag',value:calc.costPerDay},{label:'Pro Woche',value:calc.costPerWeek},{label:'Pro Jahr',value:calc.costPerYear},{label:'24 Monate',value:calc.cost2Years}].map(item => (
                <div key={item.label} style={{ textAlign: 'center', padding: '10px 4px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#38bdf8' }}>{fmtEuro(item.value)}</div>
                  <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.65)', marginTop: 2, textTransform: 'uppercase', fontWeight: 700 }}>{item.label}</div>
                </div>
              ))}
            </div>
            {calc.connectionFeeTotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '0.65rem', color: 'rgba(200,220,255,0.65)' }}>Anschlussgebühr gesamt</span>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(200,220,255,0.82)' }}>{fmtEuro(calc.connectionFeeTotal)}</span>
              </div>
            )}
            {selectedDevice && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8, marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.75)' }}>Gerät: {selectedDevice.name}</span>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>{fmtEuro(calc.devicePrice)}</span>
                </div>
                {calc.deviceMonthly > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.65rem', color: 'rgba(200,220,255,0.65)' }}>Geräte-Rate (24 Mon.)</span>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700 }}>{fmtEuro(calc.deviceMonthly)}/mtl. = {fmtEuro(calc.vertragDeviceCost)}</span>
                  </div>
                )}
                <div style={{ padding: '8px 10px', borderRadius: 10, background: calc.isBarcheaper ? 'rgba(0,245,212,0.06)' : 'rgba(255,0,110,0.06)', border: `1px solid ${calc.isBarcheaper ? 'rgba(0,245,212,0.2)' : 'rgba(255,0,110,0.2)'}`, marginTop: 6 }}>
                  <div style={{ fontSize: '0.6rem', fontWeight: 700, color: calc.isBarcheaper ? '#00f5d4' : '#ff006e' }}>
                    {calc.isBarcheaper ? '✓ Barkauf günstiger' : 'Vertrag günstiger'} — Differenz: {fmtEuro(Math.abs(calc.barDifference))}
                  </div>
                </div>
              </div>
            )}
            {calc.couponVal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <span style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.75)' }}>🎟️ Coupon-Abzug</span>
                <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#00f5d4' }}>-{fmtEuro(calc.couponVal)}</span>
              </div>
            )}
            <div style={{ borderTop: '2px solid rgba(0,245,212,0.25)', paddingTop: 10, marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 900, color: '#fff' }}>Gesamtkosten 24 Mon.</span>
              <span style={{ fontSize: '1.3rem', fontWeight: 900, background: 'linear-gradient(135deg, #00f5d4, #38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{fmtEuro(calc.totalMinusCoupon)}</span>
            </div>
          </GlassCard>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSave} style={{ flex: 1, padding: '14px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg, #38bdf8, #0284c7)', color: '#fff', fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 20px rgba(56,189,248,0.3)' }}>💾 Speichern</button>
            <button onClick={() => setActiveTab('preview')} style={{ flex: 1, padding: '14px', borderRadius: 14, border: '1px solid rgba(0,245,212,0.3)', background: 'rgba(0,245,212,0.08)', color: '#00f5d4', fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>👁️ Vorschau / PDF</button>
            <button onClick={handleClear} style={{ padding: '14px 18px', borderRadius: 14, border: '1px solid rgba(255,0,110,0.2)', background: 'rgba(255,0,110,0.06)', color: '#ff006e', fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>🗑️</button>
          </div>
        </>)}

        {/* ========== LIVEANSICHT ========== */}
        {activeTab === 'preview' && (
          <div ref={printRef} className={isGeneratingPdf ? 'pdf-export-active' : ''}>
            <GlassCard glow="cyan" style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 900, color: isGeneratingPdf ? '#000' : '#fff' }}>Angebots-Übersicht</div>
                  {kundenName && <div style={{ fontSize: '0.72rem', color: isGeneratingPdf ? '#333' : 'rgba(200,220,255,0.72)', marginTop: 2 }}>Kunde: {kundenName}</div>}
                  <div style={{ fontSize: '0.55rem', color: isGeneratingPdf ? '#666' : 'rgba(200,220,255,0.55)' }}>Erstellt am {new Date().toLocaleDateString('de-DE')} · {new Date().toLocaleTimeString('de-DE',{hour:'2-digit',minute:'2-digit'})} Uhr</div>
                </div>
                {!isGeneratingPdf && (
                  <button onClick={handleDownloadPDF} style={{ padding: '10px 18px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #38bdf8, #0284c7)', color: '#fff', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(56,189,248,0.3)' }}>PDF Laden</button>
                )}
              </div>
              {selectedTariffs.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(200,220,255,0.82)', textTransform: 'uppercase', marginBottom: 8 }}>📶 Tarife</div>
                  {selectedTariffs.map(t => (
                    <div key={t._konfId} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.02)', marginBottom: 4 }}>
                      <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff' }}>{t.title}</div>
                        <div style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.6)' }}>{typeof t.dataLabel === 'number' ? t.dataLabel + ' GB' : t.dataLabel} · {t.category}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(t.promoPrice || t.price)}/mtl.</div>
                        {t.connectionFee > 0 && <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.55)' }}>AG: {fmtEuro(t.connectionFee)}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {selectedDevice && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(200,220,255,0.82)', textTransform: 'uppercase', marginBottom: 8 }}>📱 Gerät</div>
                  <div style={{ padding: '10px 12px', borderRadius: 12, background: 'rgba(0,245,212,0.04)', border: '1px solid rgba(0,245,212,0.1)' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff' }}>{selectedDevice.name}</div>
                    <div style={{ fontSize: '0.58rem', color: 'rgba(200,220,255,0.65)', marginTop: 2 }}>
                      {deviceStorage && `${deviceStorage >= 1024 ? `${deviceStorage/1024} TB` : `${deviceStorage} GB`}`}
                      {deviceColor && ` · ${deviceColor}`}
                      {` · ${devicePayment === 'vertrag' ? 'Vertrag' : devicePayment === 'bar' ? 'Barkauf' : 'Coupon'}`}
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#00f5d4', marginTop: 4 }}>{fmtEuro(calc.devicePrice)}</div>
                  </div>
                </div>
              )}
              {selectedOptions.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(200,220,255,0.82)', textTransform: 'uppercase', marginBottom: 8 }}>⚡ Zusatzoptionen</div>
                  {selectedOptions.map(o => (
                    <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.02)', marginBottom: 3 }}>
                      <span style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.82)' }}>{o.name}</span>
                      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: o.price < 0 ? '#00f5d4' : 'rgba(200,220,255,0.82)' }}>{fmtEuro(o.price)}/mtl.</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Calculation */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(200,220,255,0.82)', textTransform: 'uppercase', marginBottom: 8 }}>📊 Kostenberechnung (24 Monate)</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                  {[{label:'Monatlich',value:calc.monthlyTotal,hl:true},{label:'Anschlussgebühr',value:calc.connectionFeeTotal},{label:'Pro Tag',value:calc.costPerDay},{label:'Pro Woche',value:calc.costPerWeek},{label:'Pro Jahr',value:calc.costPerYear},{label:'24 Monate',value:calc.cost2Years}].map(item => (
                    <div key={item.label} style={{ padding: '8px 10px', borderRadius: 10, background: item.hl ? 'rgba(0,245,212,0.06)' : 'rgba(255,255,255,0.02)', border: `1px solid ${item.hl ? 'rgba(0,245,212,0.15)' : 'rgba(255,255,255,0.04)'}` }}>
                      <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.6)', textTransform: 'uppercase', fontWeight: 700 }}>{item.label}</div>
                      <div style={{ fontSize: '0.82rem', fontWeight: 800, color: item.hl ? '#00f5d4' : '#fff' }}>{fmtEuro(item.value)}</div>
                    </div>
                  ))}
                </div>
                {calc.couponVal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, padding: '8px 10px', borderRadius: 10, background: 'rgba(0,245,212,0.06)' }}>
                    <span style={{ fontSize: '0.68rem', color: '#00f5d4', fontWeight: 700 }}>🎟️ Coupon</span>
                    <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#00f5d4' }}>-{fmtEuro(calc.couponVal)}</span>
                  </div>
                )}
                <div style={{ borderTop: '2px solid rgba(0,245,212,0.25)', paddingTop: 10, marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.88rem', fontWeight: 900 }}>Gesamtkosten</span>
                  <span style={{ fontSize: '1.4rem', fontWeight: 900, background: 'linear-gradient(135deg, #00f5d4, #38bdf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{fmtEuro(calc.totalMinusCoupon)}</span>
                </div>
              </div>
              {notiz && (
                <div style={{ marginTop: 12, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.55)', fontWeight: 700 }}>NOTIZ</div>
                  <div style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.82)' }}>{notiz}</div>
                </div>
              )}
            </GlassCard>
          </div>
        )}

        {/* ========== SAVED TAB ========== */}
        {activeTab === 'saved' && (
          <GlassCard glow="purple">
            <SectionLabel icon="📂">Gespeicherte Angebote ({savedOffers.length})</SectionLabel>
            {savedOffers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'rgba(200,220,255,0.5)', fontSize: '0.72rem' }}>Noch keine Angebote gespeichert</div>
            ) : savedOffers.map(o => (
              <div key={o.id} style={{ padding: '12px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>{o.kundenName}</div>
                    <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.6)' }}>
                      {new Date(o.timestamp).toLocaleDateString('de-DE')} · {(o.selectedTariffs||[]).length} Tarif(e) · {o.device ? o.device.name : 'Kein Gerät'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(o.calc?.totalMinusCoupon || 0)}</div>
                    <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>24 Mon.</div>
                  </div>
                </div>
                <button onClick={() => handleDelete(o.id)} style={{ marginTop: 6, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,0,110,0.2)', background: 'rgba(255,0,110,0.06)', color: '#ff006e', fontSize: '0.55rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>🗑️ Löschen</button>
              </div>
            ))}
          </GlassCard>
        )}
      </div>
{/* 
      // Der Style-Block unten ändert für den html2pdf Renderer die Eigenschaften.
      // Falls isGeneratingPdf === true setzen wir eine feste Breite ohne Darkmode-Schriftfarben für sauberen weißen Druck. 
*/}
      {isGeneratingPdf && (
        <style>{`
          .pdf-export-active * { 
            color: #000 !important; 
            text-shadow: none !important; 
            -webkit-text-fill-color: #000 !important; 
          }
          .pdf-export-active {
            background: #fff !important;
            padding: 20px !important;
          }
        `}</style>
      )}
    </div>
  );
}

export default AngebotKonfigurator;
